// Snapshot page shell. One scrolling body under a fixed run bar; the same
// sections in the same order for both network shapes. The feature flag
// branches only the network diagram and section 4 (rebalance cards vs the
// by-location table) — everything else is shared.
import CircularProgress from "@mui/material/CircularProgress";
import { SnapshotPeriod, SnapshotViewModel } from "./types";
import { ChannelCards } from "./channel_cards";
import { RunBar } from "./run_bar";
import { KpiRail } from "./kpi_rail";
import { NetworkMapHubSpoke } from "./network_map_hub_spoke";
import { NetworkMapFlat } from "./network_map_flat";
import { FlowToSales } from "./flow_to_sales";
import { RebalanceSignal } from "./rebalance_signal";
import { LocationTable } from "./location_table";
import { TrailingStrip } from "./trailing_strip";
import { RiskStack } from "./risk_tables";
import { SectionTitle, SnapCard, useSnapshotTokens } from "./primitives";
import { CoverageBanner } from "./coverage_banner";
import { DroppedLocationCheck } from "./data_quality";

/** Names locations left out for having no stock and no forecast demand.
 *  Hiding rows silently would make a network look smaller than it is. */
const emptyLocationsNote = (hidden: number): string =>
  hidden > 0
    ? ` · ${hidden} empty ${hidden === 1 ? "location" : "locations"} hidden`
    : "";

const Legend = ({
  isHubAndSpoke,
  period,
}: {
  isHubAndSpoke: boolean;
  period: SnapshotPeriod;
}) => {
  const t = useSnapshotTokens();
  const ring = [
    { label: "On target", c: t.greenInk },
    { label: "Light vs target", c: t.orangeInk },
    { label: "Under target", c: t.redInk },
  ];
  return (
    <div
      style={{
        display: "flex",
        gap: 18,
        marginTop: 4,
        paddingTop: 12,
        borderTop: `1px solid ${t.line2}`,
        flexWrap: "wrap",
      }}
    >
      {ring.map((x) => (
        <span
          key={x.label}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontSize: 11,
            color: t.ink3,
          }}
        >
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: 3,
              border: `2px solid ${x.c}`,
            }}
          />
          {x.label}
        </span>
      ))}
      <div style={{ flex: 1 }} />
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          fontSize: 11,
          color: t.ink3,
        }}
      >
        <span
          style={{
            width: 16,
            height: 6,
            borderRadius: 3,
            background: t.blue,
            opacity: 0.42,
          }}
        />
        {isHubAndSpoke ? "Inbound / transfer volume" : "Units inbound"}
      </span>
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          fontSize: 11,
          color: t.ink3,
        }}
      >
        <span
          style={{
            width: 16,
            height: 6,
            borderRadius: 3,
            background: t.greenInk,
            opacity: 0.3,
          }}
        />
        {period === "month" ? "Monthly" : "Weekly"} outflow to sales
      </span>
    </div>
  );
};

export const RunReportCardComponent = ({
  loading,
  model,
  coverageCheck,
  weekLabel,
  monthLabel,
  monthWeeks,
  emptyLocationsHidden,
  onPeriodChange,
}: {
  loading: boolean;
  model: SnapshotViewModel | null;
  coverageCheck: DroppedLocationCheck;
  weekLabel: string;
  monthLabel: string;
  monthWeeks: number;
  emptyLocationsHidden: number;
  onPeriodChange: (next: SnapshotPeriod) => void;
}) => {
  const t = useSnapshotTokens();
  // Only blank the page when there is nothing to show yet. Switching period
  // reloads flow data, but the stock figures on screen are unaffected by it
  // — replacing them with a spinner would imply they might change.
  if (!model) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
        }}
      >
        {loading ? (
          <CircularProgress size={28} />
        ) : (
          <span style={{ fontSize: 13, color: t.ink3 }}>
            No completed run to report on yet — trigger a run from Run History.
          </span>
        )}
      </div>
    );
  }

  const hubs = model.locations.filter((l) => l.role === "hub");
  const spokes = model.locations.filter((l) => l.role === "spoke");

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        background: t.surface2,
        fontVariantNumeric: "tabular-nums",
      }}
    >
      <RunBar
        run={model.run}
        period={model.period}
        weekLabel={weekLabel}
        monthLabel={monthLabel}
        monthWeeks={monthWeeks}
        onPeriodChange={onPeriodChange}
        loading={loading}
      />
      <div
        style={{
          flex: 1,
          overflow: "auto",
          padding: "18px 22px 28px",
          display: "flex",
          flexDirection: "column",
          gap: 20,
        }}
      >
        {/* 1b. Data coverage warning — stock the plan cannot see */}
        <CoverageBanner check={coverageCheck} />

        {/* 2. Network state: diagram + KPI rail */}
        <div>
          <SectionTitle
            note={
              "Point-in-time, " +
              model.run.asOf +
              (model.isHubAndSpoke
                ? " · border color is weeks of supply against each location's target"
                : " · peer DCs, each replenished directly from vendors") +
              emptyLocationsNote(emptyLocationsHidden)
            }
          >
            Network state
          </SectionTitle>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 1fr) 268px",
              gap: 14,
              alignItems: "stretch",
            }}
          >
            <SnapCard pad={18} style={{ overflow: "hidden" }}>
              {model.isHubAndSpoke && hubs.length > 0 ? (
                <NetworkMapHubSpoke
                  hubs={hubs}
                  spokes={spokes}
                  lanes={model.transferLanes}
                  inbound={model.inbound}
                  sales={model.sales}
                  channels={model.channels}
                  period={model.period}
                />
              ) : (
                <NetworkMapFlat
                  locations={model.locations}
                  inbound={model.inbound}
                  sales={model.sales}
                  channels={model.channels}
                  period={model.period}
                />
              )}
              <Legend
                isHubAndSpoke={model.isHubAndSpoke}
                period={model.period}
              />
            </SnapCard>
            <SnapCard pad={16}>
              <KpiRail kpis={model.kpis} inStockTrend={model.inStockTrend} />
            </SnapCard>
          </div>
        </div>

        {/* 3. Flow to sales */}
        <div>
          <SectionTitle
            note={
              model.period === "month"
                ? "How much of what's on hand has left for customers so far this month"
                : "How much of what's on hand is leaving for customers each week"
            }
          >
            Flow to sales
          </SectionTitle>
          <FlowToSales
            locations={model.locations}
            sales={model.sales}
            channels={model.channels}
            period={model.period}
          />
        </div>

        {/* 3b. Channel cards */}
        <div>
          <SectionTitle
            note={
              model.period === "month"
                ? "Where the month's outflow went"
                : "Where the week's outflow went"
            }
          >
            Sales channels
          </SectionTitle>
          <ChannelCards
            channels={model.channels}
            locations={model.locations}
            period={model.period}
            demandPath="/demand"
          />
        </div>

        {/* 4. Rebalance signal (hub) / By location (flat) */}
        {model.isHubAndSpoke ? (
          <div>
            <SectionTitle note="Hub → spoke moves this run recommends">
              Rebalance signal
            </SectionTitle>
            <SnapCard pad={14}>
              <RebalanceSignal
                cards={model.rebalance}
                transferPlanningPath={model.links.transferPlanning}
              />
            </SnapCard>
          </div>
        ) : (
          <div>
            <SectionTitle
              note={
                "Same rows as the map, for reading and sorting. No transfer lanes — coverage is fixed by purchasing." +
                emptyLocationsNote(emptyLocationsHidden)
              }
            >
              By location
            </SectionTitle>
            <LocationTable
              locations={model.locations}
              inbound={model.inbound}
              inventoryHealthPath={model.links.inventoryHealth}
              period={model.period}
            />
          </div>
        )}

        {/* 5. Trailing actuals */}
        <TrailingStrip metrics={model.trailing} asOf={model.run.asOf} />

        {/* 6. Risks */}
        <RiskStack
          stockOuts={model.stockOuts}
          lowStock={model.lowStock}
          excess={model.excess}
          isHubAndSpoke={model.isHubAndSpoke}
          inventoryPath={model.links.inventory}
          inventoryHealthPath={model.links.inventoryHealth}
          transferPlanningPath={model.links.transferPlanning}
          // Single-channel customers (goodr, goodchop) get no channel split:
          // it would just restate the SKU's own demand.
          showChannels={
            model.channels.filter((c) => !c.isOther).length > 1
          }
        />
      </div>
    </div>
  );
};
